import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { useBlocNext } from "@blac/react";
import TourGuideCubit from "./TourGuideCubit";
import TourGuideTooltip from "./TourGuideTooltip";
import { Backdrop, HighlightRing, TooltipArrow } from "./tourGuide.styles";
import {
  TOUR_PORTAL_ID,
  ARROW_ROTATION,
  SLIDE_ENTER,
  SLIDE_EXIT,
  SPRING_ENTER,
  QUICK_EXIT,
  BACKDROP_ENTER_TRANSITION,
  BACKDROP_EXIT_TRANSITION,
  RING_INITIAL,
  RING_ENTER,
  RING_EXIT
} from "./constants";
import { DEFAULT_PLACEMENT } from "./types";

const ARROW_SVG = (
  <svg
    width="16"
    height="8"
    viewBox="0 0 16 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.43157 0.603292C8.64672 -0.201178 7.35328 -0.201179 6.56844 0.603291L0 7.33594L16 7.33594L9.43157 0.603292Z"
      fill="currentColor"
    />
  </svg>
);

const TourGuideOverlay: FC = () => {
  const [{ activeTourId, steps, currentStepIndex, isTransitioning }, cubit] =
    useBlocNext(TourGuideCubit);

  const portalRef = useRef<HTMLElement | null>(null);
  const [layoutReady, setLayoutReady] = useState(false);

  const handleNext = useCallback((): void => {
    void cubit.next();
  }, [cubit]);

  const handleDismiss = useCallback((): void => {
    void cubit.dismiss();
  }, [cubit]);

  useEffect(() => {
    if (!activeTourId) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        handleDismiss();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeTourId, handleDismiss]);

  const currentStep =
    activeTourId && steps.length > 0 ? steps[currentStepIndex] : undefined;

  const anchorReady = !!(
    currentStep && cubit.isAnchorRegistered(currentStep.anchorName)
  );

  const showBackdrop = !!(activeTourId && steps.length > 0);
  const showStep = showBackdrop && !isTransitioning && anchorReady;

  useEffect(() => {
    if (!showStep || !currentStep) {
      setLayoutReady(false);
      return;
    }

    const anchorEl = document.querySelector<HTMLElement>(
      `[data-tour-anchor="${currentStep.anchorName}"]`
    );

    if (!anchorEl) {
      console.error(
        `[TourGuide] Anchor element "${currentStep.anchorName}" not found in DOM — exiting tour.`
      );
      void cubit.dismiss();
      return;
    }

    anchorEl.scrollIntoView({ behavior: "instant", block: "nearest" });

    const id = requestAnimationFrame(() => {
      setLayoutReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, [showStep, currentStepIndex, currentStep, cubit]);

  // Resolve portal target (lazy, cached in ref)
  if (!portalRef.current) {
    portalRef.current = document.getElementById(TOUR_PORTAL_ID);
  }
  if (!portalRef.current) {
    return null;
  }

  const anchorMeta = currentStep
    ? cubit.getAnchorMeta(currentStep.anchorName)
    : undefined;
  const placement = currentStep?.placement ?? DEFAULT_PLACEMENT;
  const rotation = ARROW_ROTATION[placement];

  return createPortal(
    <>
      <AnimatePresence>
        {showBackdrop && (
          <Backdrop
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: BACKDROP_ENTER_TRANSITION }}
            exit={{ opacity: 0, transition: BACKDROP_EXIT_TRANSITION }}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showStep && currentStep && (
          <HighlightRing
            key={`ring-${currentStepIndex}`}
            anchorName={currentStep.anchorName}
            borderRadius={anchorMeta?.borderRadius}
            onClick={handleNext}
            initial={RING_INITIAL}
            animate={layoutReady ? RING_ENTER : RING_INITIAL}
            exit={RING_EXIT}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showStep && currentStep && (
          <TooltipArrow
            key={`arrow-${currentStepIndex}`}
            anchorName={currentStep.anchorName}
            placement={placement}
            initial={{
              opacity: 0,
              rotate: rotation,
              ...SLIDE_ENTER[placement]
            }}
            animate={
              layoutReady
                ? {
                    opacity: 1,
                    rotate: rotation,
                    x: 0,
                    y: 0,
                    transition: SPRING_ENTER
                  }
                : { opacity: 0, rotate: rotation, ...SLIDE_ENTER[placement] }
            }
            exit={{
              opacity: 0,
              rotate: rotation,
              ...SLIDE_EXIT[placement],
              transition: QUICK_EXIT
            }}
            aria-hidden="true"
          >
            {ARROW_SVG}
          </TooltipArrow>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showStep && currentStep && (
          <TourGuideTooltip
            key={`tooltip-${currentStepIndex}`}
            step={currentStep}
            stepIndex={currentStepIndex}
            totalSteps={steps.length}
            onNext={handleNext}
            layoutReady={layoutReady}
          />
        )}
      </AnimatePresence>
    </>,
    portalRef.current
  );
};

export default TourGuideOverlay;
